if (!customElements.get('product-card-quantity')) {
  customElements.define('product-card-quantity', class ProductCardQuantity extends HTMLElement {
    constructor() {
      super();

      this.qty = this.querySelector('.qty-selector');
      this.minus = this.querySelector('.qty-minus');
      this.plus = this.querySelector('.qty-plus');

      if (!this.qty || !this.minus || !this.plus) return;

      this.variantId = String(this.dataset.variantId);
      this.min = parseInt(this.qty.getAttribute('min'), 10) || 0;
      this.max = parseInt(this.qty.getAttribute('max'), 10) || 999;
      this.isUpdating = false;
      this.isEditing = false;
      this.updateToken = 0;
      this.lastAppliedQty = parseInt(this.qty.value, 10) || 0;

      this.update = debounce(this.updateCart.bind(this), 400);

      this.minus.addEventListener('pointerdown', event => {
        event.stopPropagation();
        ProductCardQuantity.markQuickBuyInteraction();
      });

      this.plus.addEventListener('pointerdown', event => {
        event.stopPropagation();
        ProductCardQuantity.markQuickBuyInteraction();
      });

      this.minus.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        ProductCardQuantity.markQuickBuyInteraction();
        this.setQuantity((parseInt(this.qty.value, 10) || 0) - 1);
      });

      this.plus.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        ProductCardQuantity.markQuickBuyInteraction();
        this.setQuantity((parseInt(this.qty.value, 10) || 0) + 1);
      });

      this.qty.addEventListener('pointerdown', event => {
        event.stopPropagation();
        ProductCardQuantity.markQuickBuyInteraction();
      });

      this.qty.addEventListener('click', event => {
        event.stopPropagation();
        ProductCardQuantity.markQuickBuyInteraction();

        this.qty.focus();

        setTimeout(() => {
          this.qty.select();
        }, 0);
      });

      this.qty.addEventListener('focus', () => {
        this.isEditing = true;

        setTimeout(() => {
          this.qty.select();
        }, 0);
      });

      this.qty.addEventListener('input', () => {
        this.isEditing = true;
        ProductCardQuantity.markQuickBuyInteraction();
        this.setButtonState();
      });

      this.qty.addEventListener('change', () => {
        this.commitInputQuantity();
      });

      this.qty.addEventListener('blur', () => {
        if (!this.isEditing) return;

        this.commitInputQuantity();
      });

      this.qty.addEventListener('keydown', event => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        event.stopPropagation();

        this.commitInputQuantity();
        this.qty.blur();
      });

      this.setButtonState();
      ProductCardQuantity.bindCartSyncListener();
      ProductCardQuantity.bindQuickBuyInteractionListener();

      if (!window.productCardQuantityInitialSyncDone) {
        window.productCardQuantityInitialSyncDone = true;
        ProductCardQuantity.syncAll();
      }
    }

    commitInputQuantity() {
      this.isEditing = false;
      ProductCardQuantity.markQuickBuyInteraction();

      let quantity = parseInt(this.qty.value, 10);

      if (Number.isNaN(quantity)) {
        quantity = this.min;
      }

      quantity = Math.max(this.min, Math.min(quantity, this.max));

      this.qty.value = quantity;
      this.setButtonState();

      if (quantity !== this.lastAppliedQty) {
        this.lastAppliedQty = quantity;
        this.update(quantity);
      }
    }

    setQuantity(quantity) {
      quantity = Math.max(this.min, Math.min(quantity, this.max));

      this.qty.value = quantity;
      this.setButtonState();

      if (quantity !== this.lastAppliedQty) {
        this.lastAppliedQty = quantity;
        this.update(quantity);
      }
    }

    setButtonState() {
      const quantity = parseInt(this.qty.value, 10) || 0;

      this.minus.classList.toggle('disabled', quantity <= this.min);
      this.plus.classList.toggle('disabled', quantity >= this.max);
    }

    async updateCart(quantity) {
      this.isUpdating = true;
      this.updateToken += 1;

      const currentToken = this.updateToken;

      this.classList.add('processing');

      try {
        const cart = await ProductCardQuantity.getCart();

        const existingItems = cart.items.filter(item => {
          return String(item.variant_id) === this.variantId;
        });

        const existingItem = existingItems[0] || null;

        const previousQuantity = existingItems.reduce((total, item) => {
          return total + (parseInt(item.quantity, 10) || 0);
        }, 0);

        const addedQuantity = Math.max(0, quantity - previousQuantity);

        let url;
        let body;

        if (quantity === previousQuantity) {
          return;
        }

        if (quantity > previousQuantity) {
          const quantityToAdd = quantity - previousQuantity;

          url = `${KROWN.settings.routes.cart_add_url}.js`;
          body = JSON.stringify({
            id: this.variantId,
            quantity: quantityToAdd
          });
        } else if (existingItems.length === 1) {
          url = KROWN.settings.routes.cart_change_url;
          body = JSON.stringify({
            id: existingItem.key,
            quantity
          });
        } else {
          await ProductCardQuantity.setTotalQuantity(
            existingItems,
            quantity
          );

          if (currentToken !== this.updateToken) return;

          if (window.refreshCart) {
            window.refreshCart(false);
          }

          await ProductCardQuantity.syncAll();
          return;
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/javascript'
          },
          body
        }).then(response => response.json());

        if (response.status == 422) {
          throw new Error(response.description || response.message);
        }

        if (currentToken !== this.updateToken) {
          return;
        }

        if (window.refreshCart) {
          window.refreshCart(false);
        }

        const updatedCart = await ProductCardQuantity.syncAll();

        if (addedQuantity > 0) {
          ProductCardQuantity.runSuccessAnimation({
            sourceElement: this,
            addedQuantity,
            previousCart: cart,
            cart: updatedCart
          });
        }

      } catch (error) {
        console.log(error);
      } finally {
        if (currentToken === this.updateToken) {
          this.isUpdating = false;
          this.classList.remove('processing');
        }
      }
    }

    static async setTotalQuantity(items, desiredQuantity) {
      let quantityRemaining = desiredQuantity;
      const updates = {};

      items.forEach(item => {
        const currentQuantity = parseInt(item.quantity, 10) || 0;
        const nextQuantity = Math.min(currentQuantity, quantityRemaining);

        updates[item.key] = nextQuantity;
        quantityRemaining -= nextQuantity;
      });

      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ updates })
      }).then(response => response.json());
    }

    static async getCart() {
      return fetch(`${KROWN.settings.routes.cart_url}.js`)
        .then(response => response.json());
    }

    static async syncAll() {
      const cart = await ProductCardQuantity.getCart();

      document.querySelectorAll('product-card-quantity').forEach(card => {
        if (card.isUpdating || card.isEditing) return;

        const matchingItems = cart.items.filter(item => {
          return String(item.variant_id) === String(card.dataset.variantId);
        });

        const totalQuantity = matchingItems.reduce((total, item) => {
          return total + (parseInt(item.quantity, 10) || 0);
        }, 0);

        const input = card.querySelector('.qty-selector');
        const minus = card.querySelector('.qty-minus');
        const plus = card.querySelector('.qty-plus');

        if (!input || !minus || !plus) return;

        input.value = totalQuantity;
        card.lastAppliedQty = totalQuantity;

        const quantity = parseInt(input.value, 10) || 0;
        const min = parseInt(input.getAttribute('min'), 10) || 0;
        const max = parseInt(input.getAttribute('max'), 10) || 999;

        minus.classList.toggle('disabled', quantity <= min);
        plus.classList.toggle('disabled', quantity >= max);
      });

      return cart;
    }

    static getAnimationSettings() {
      const settings = window.KROWN && window.KROWN.settings ? window.KROWN.settings : {};

      return {
        type: settings.quick_buy_success_animation || 'cart_popup',
        drawerDelay: parseInt(settings.quick_buy_cart_drawer_delay, 10) || 0,
        popupDuration: parseInt(settings.quick_buy_popup_duration, 10) || 2000
      };
    }

    static getCartTrigger() {
      const selectors = [
        '.button--cart-handle',
        '.mobile-cart-button',
        '[aria-controls="site-cart-sidebar"]',
        'a[href$="/cart"]',
        'a[href*="/cart?"]'
      ];

      const candidates = Array.from(document.querySelectorAll(selectors.join(',')));

      return candidates.find(element => {
        const rect = element.getBoundingClientRect();
        const style = window.getComputedStyle(element);

        return (
          rect.width > 0 &&
          rect.height > 0 &&
          rect.bottom >= 0 &&
          rect.top <= window.innerHeight &&
          style.visibility !== 'hidden' &&
          style.display !== 'none' &&
          style.opacity !== '0'
        );
      }) || candidates[0] || null;
    }

    static runSuccessAnimation(detail = {}) {
      const settings = ProductCardQuantity.getAnimationSettings();
      const type = settings.type;

      if (
        window.AppsalonFreeShippingCelebration &&
        typeof window.AppsalonFreeShippingCelebration.handleKnownCartChange === 'function' &&
        window.AppsalonFreeShippingCelebration.handleKnownCartChange(detail.previousCart, detail.cart)
      ) {
        ProductCardQuantity.cancelCartDrawer();
        return;
      }

      if (type !== 'cart_drawer') {
        ProductCardQuantity.cancelCartDrawer();
      }

      if (type === 'none') return;

      if (type === 'cart_drawer') {
        ProductCardQuantity.openCartDrawer(settings.drawerDelay);
        ProductCardQuantity.pulseCartIcon();
        return;
      }

      ProductCardQuantity.showCartPopup(detail, settings.popupDuration);
    }

    static cancelCartDrawer() {
      window.clearTimeout(ProductCardQuantity.drawerTimer);
      ProductCardQuantity.drawerTimer = null;
      ProductCardQuantity.pendingDrawerDelay = null;
    }

    static openCartDrawer(delay = 0, options = {}) {
      if (document.body.classList.contains('template-cart')) return;

      const idleDelay = Math.max(0, parseInt(delay, 10) || 0);

      ProductCardQuantity.cancelCartDrawer();
      ProductCardQuantity.pendingDrawerDelay = idleDelay;

      const attemptOpen = () => {
        const now = Date.now();
        const lastInteractionAt = ProductCardQuantity.lastQuickBuyInteractionAt || 0;
        const timeSinceLastInteraction = now - lastInteractionAt;

        if (ProductCardQuantity.quickBuyPointerDown) {
          ProductCardQuantity.drawerTimer = window.setTimeout(attemptOpen, 150);
          return;
        }

        if (idleDelay > 0 && timeSinceLastInteraction < idleDelay) {
          ProductCardQuantity.drawerTimer = window.setTimeout(
            attemptOpen,
            Math.max(150, idleDelay - timeSinceLastInteraction)
          );
          return;
        }

        ProductCardQuantity.drawerTimer = null;
        ProductCardQuantity.pendingDrawerDelay = null;

        const cartDrawer = document.getElementById('site-cart-sidebar');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show();
          return;
        }

        const cartTrigger = ProductCardQuantity.getCartTrigger();
        if (cartTrigger) {
          cartTrigger.click();
        }
      };

      ProductCardQuantity.drawerTimer = window.setTimeout(attemptOpen, idleDelay);
    }

    static pulseCartIcon() {
      const cartTrigger = ProductCardQuantity.getCartTrigger();
      if (!cartTrigger) return;

      cartTrigger.classList.remove('appsalon-cart-pulse');
      void cartTrigger.offsetWidth;
      cartTrigger.classList.add('appsalon-cart-pulse');

      window.setTimeout(() => {
        cartTrigger.classList.remove('appsalon-cart-pulse');
      }, 900);
    }

    static showCartPopup(detail = {}, duration = 2000) {
      const cartTrigger = ProductCardQuantity.getCartTrigger();

      ProductCardQuantity.pulseCartIcon();

      let popup = document.querySelector('[data-appsalon-quick-buy-popup]');
      if (!popup) {
        popup = document.createElement('div');
        popup.setAttribute('data-appsalon-quick-buy-popup', '');
        popup.className = 'appsalon-quick-buy-popup';
        popup.setAttribute('role', 'status');
        popup.setAttribute('aria-live', 'polite');
        document.body.appendChild(popup);
      }

      const addedQuantity = parseInt(detail.addedQuantity, 10) || 1;
      popup.innerHTML = `
        <span class="appsalon-quick-buy-popup__icon" aria-hidden="true">✓</span>
        <span class="appsalon-quick-buy-popup__text">Lagt i handlekurven${addedQuantity > 1 ? ` × ${addedQuantity}` : ''}</span>
      `;

      popup.style.visibility = 'hidden';
      popup.classList.remove('is-visible');
      popup.style.removeProperty('right');
      popup.style.removeProperty('bottom');

      const popupWidth = popup.offsetWidth || 260;
      const popupHeight = popup.offsetHeight || 48;
      const margin = 12;
      let top = margin;
      let left = window.innerWidth - popupWidth - margin;

      if (cartTrigger) {
        const triggerRect = cartTrigger.getBoundingClientRect();

        if (triggerRect.width > 0 && triggerRect.height > 0) {
          top = triggerRect.bottom + 10;
          left = triggerRect.right - popupWidth;
        }
      }

      top = Math.max(margin, Math.min(top, window.innerHeight - popupHeight - margin));
      left = Math.max(margin, Math.min(left, window.innerWidth - popupWidth - margin));

      popup.style.top = `${top}px`;
      popup.style.left = `${left}px`;
      popup.style.visibility = '';

      void popup.offsetWidth;
      popup.classList.add('is-visible');

      window.clearTimeout(ProductCardQuantity.popupTimer);
      ProductCardQuantity.popupTimer = window.setTimeout(() => {
        popup.classList.remove('is-visible');
      }, Math.max(1000, parseInt(duration, 10) || 2000));
    }

    static markQuickBuyInteraction() {
      ProductCardQuantity.lastQuickBuyInteractionAt = Date.now();
    }

    static bindQuickBuyInteractionListener() {
      if (window.productCardQuantityInteractionBound) return;

      window.productCardQuantityInteractionBound = true;

      document.addEventListener('pointerdown', event => {
        if (!event.target.closest('product-card-quantity')) return;

        ProductCardQuantity.quickBuyPointerDown = true;
        ProductCardQuantity.markQuickBuyInteraction();
      }, true);

      document.addEventListener('pointerup', () => {
        ProductCardQuantity.quickBuyPointerDown = false;
      }, true);

      document.addEventListener('pointercancel', () => {
        ProductCardQuantity.quickBuyPointerDown = false;
      }, true);

      document.addEventListener('click', event => {
        if (!event.target.closest('product-card-quantity')) return;
        ProductCardQuantity.markQuickBuyInteraction();
      }, true);

      document.addEventListener('input', event => {
        if (!event.target.closest('product-card-quantity')) return;
        ProductCardQuantity.markQuickBuyInteraction();
      }, true);

      document.addEventListener('keydown', event => {
        if (!event.target.closest('product-card-quantity')) return;
        ProductCardQuantity.markQuickBuyInteraction();
      }, true);
    }

    static bindCartSyncListener() {
      if (window.productCardQuantityCartSyncBound) return;

      window.productCardQuantityCartSyncBound = true;

      document.addEventListener('click', event => {
        if (
          event.target.closest('[data-js-cart-item]') ||
          event.target.closest('#AjaxCartForm') ||
          event.target.closest('[data-js-site-cart-sidebar]')
        ) {
          setTimeout(() => {
            ProductCardQuantity.syncAll();
          }, 700);
        }
      });

      document.addEventListener('input', event => {
        if (
          event.target.closest('[data-js-cart-item]') ||
          event.target.closest('#AjaxCartForm')
        ) {
          setTimeout(() => {
            ProductCardQuantity.syncAll();
          }, 900);
        }
      });
    }
  });
}
